import { useEffect, useRef, useState, useCallback } from "react";
import { useDelayedSwap } from "./useDelayedSwap";
import { useSelectiveClickOutside } from "./useSelectiveOutsideClick";
import { useLatest } from "./useLatest";

/**
 * Shared popover state management hook.
 *
 * Encapsulates the delayed-swap animation, anchor/position tracking, and
 * click-outside handling that both CubePopover and TimelinePopover need.
 *
 * Parameters:
 *   itemKey        {any}     — the selected key (info_key for cubes, year for timeline)
 *   itemPosition   {object}  — position rect of the clicked element { x, y, width, height }
 *   anchorName     {string}  — CSS anchor name of the clicked element (e.g. "--cube-0-0")
 *   defaultAnchor  {string}  — fallback anchor value used before first click
 *   ignoreSelector {string}  — CSS selector for click-outside exclusion
 *   onClose        {function}— external close callback (optional)
 *
 * Returns:
 *   displayedKey      — the currently displayed key (after delayed swap)
 *   isClosing         — whether the close animation is in progress
 *   handleClose       — unified close handler
 *   displayedPosition — position of the element whose content is displayed
 *   anchor            — current CSS anchor name
 *   containerRef      — ref to attach to the popover container element
 */
export function usePopoverState({
  itemKey,
  itemPosition,
  anchorName,
  defaultAnchor,
  ignoreSelector,
  onClose,
}) {
  const containerRef = useRef(null);

  // Keep latest values around without triggering immediate state updates
  const latestItemPos = useLatest(itemPosition);
  const latestAnchor = useLatest(anchorName);

  // Manage "close then swap content" behavior
  const { displayed: displayedKey, isClosing, close } = useDelayedSwap({
    value: itemKey,
    delay: 300,
  });

  const [displayedPosition, setDisplayedPosition] = useState(null);
  const [anchor, setAnchor] = useState(defaultAnchor);

  // When the displayed key changes (after delayed swap), apply latest position + anchor
  useEffect(() => {
    if (!displayedKey) return;
    if (latestItemPos.current) setDisplayedPosition(latestItemPos.current);
    if (latestAnchor.current) setAnchor(latestAnchor.current);
  }, [displayedKey, latestItemPos, latestAnchor]);

  const handleClose = useCallback(() => {
    close();
    setTimeout(() => {
      setDisplayedPosition(null);
      onClose?.();
    }, 300);
  }, [close, onClose]);

  useSelectiveClickOutside({
    enabled: Boolean(displayedKey),
    containerRef,
    ignoreSelector,
    onOutside: handleClose,
  });

  return {
    displayedKey,
    isClosing,
    handleClose,
    displayedPosition,
    anchor,
    containerRef,
  };
}
